import { Button, Checkbox, Form, Input, Space, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';

import { apiFetch } from '@/api/client';
import { isUnknownCompany, resolveTenant, throttledFor } from '@/api/tenantDiscovery';
import { useNotify } from '@/api/notify';
import { AuthLayout } from '@/components/AuthLayout';
import type { SignedInUser } from '@core/session/types';
import type { TenantEndpoint } from '@/session/tenantEndpoint';
import { fontMono, palette } from '@/theme';

interface AuthResponse {
  accessToken: string;
  user: SignedInUser;
}

/**
 * The other thing `POST /api/auth/login` can answer: the password was right, and the account wants
 * a code too.
 *
 * Both shapes come back as 200, so the client discriminates on `twoFactorRequired` rather than on
 * the status. A 401 here would be wrong — nothing failed — and would send the shared "your session
 * expired" handling down a path that ends in the user being told to sign in, which is what they
 * were in the middle of doing.
 */
interface TwoFactorChallengeResponse {
  twoFactorRequired: true;
  challengeToken: string;
  expiresOn: string;
}

type LoginResponse = AuthResponse | TwoFactorChallengeResponse;

/**
 * A challenge in flight, plus the "Remember me" the user ticked on the step before it.
 *
 * The server does not hold that choice between the two steps — it is the user's own preference
 * about their own browser and grants nothing, so restating it on the exchange is safe and saves a
 * column on a table shared with email verification and password reset. Carrying it here is what
 * stops a 2FA account silently losing the tick it made a moment ago.
 */
interface PendingChallenge extends TwoFactorChallengeResponse {
  rememberMe: boolean;
}

function isChallenge(response: LoginResponse): response is TwoFactorChallengeResponse {
  return 'twoFactorRequired' in response;
}

interface Credentials {
  /** A username or a mobile number — the server decides which by looking. */
  identifier: string;
  password: string;

  /**
   * Whether this browser should keep the session after it closes.
   *
   * Off by default, deliberately. The machine this product runs on is a shop counter shared by
   * whoever is on shift, and a fortnight-long session left on it is the sort of default that is
   * only ever noticed after it matters. Staff on their own device can opt in.
   */
  rememberMe: boolean;
}

export interface SignInPageProps {
  /**
   * The company this browser has already resolved, or null to start at the company step.
   *
   * Held by the caller rather than read from storage here, because the same value configures the
   * API client — one owner, so the screen and the client can never disagree about which server is
   * being talked to.
   */
  tenant: TenantEndpoint | null;

  /** A company was resolved. The caller persists it and points the API client at it. */
  onTenantResolved: (tenant: TenantEndpoint) => void;

  /** "Change company" — back to step one, forgetting what was remembered. */
  onChangeTenant: () => void;

  onSignedIn: (auth: AuthResponse) => void;
}

/**
 * Sign-in, in up to three steps: which company, then who, then — for an account with 2FA on — a
 * code.
 *
 * The company/credentials split exists because this is a SaaS platform where a customer may be
 * served by their own API deployment. Nothing can authenticate against the right server until it
 * knows which server that is, so resolving the company code is a separate, pre-credential round
 * trip by necessity — not for presentation.
 *
 * Two consequences worth naming, since both look like design choices and are really constraints:
 *
 * - **The company step confirms a company by name.** That tells an anonymous caller which company
 *   codes are real, which the old single-step form deliberately avoided by answering every failure
 *   identically. It is an accepted trade for this flow; the server rations wrong guesses by
 *   address (three, then an hour) rather than pretending the trade isn't being made.
 * - **The company is remembered on the device.** A counter clerk opening the till each morning
 *   should not re-type it. Nothing stored is a credential — a company code and a trading name are
 *   on the shop's own paperwork.
 *
 * The third step only exists once credentials come back with `twoFactorRequired` rather than a
 * session. It replaces the credentials step rather than sitting alongside it — the challenge is
 * held in this component's state and nowhere else (not storage, not a cookie), since it lives for
 * five minutes, is single-use, and opens nothing without a code.
 *
 * The layout is the template's `login-illustration` variant, now shared with the reset-password
 * screen as `AuthLayout` — see that file for why the panel is a glyph on a gradient rather than an
 * image, and why it disappears below `lg`.
 *
 * All three steps render into that one column, so the screen reads as one thing progressing rather
 * than as screens replacing each other. The icon is a coat hanger rather than the template's rocket
 * because this is a garment-rental product and the panel is the one place on the screen that says
 * what the product is.
 */
export function SignInPage({
  tenant,
  onTenantResolved,
  onChangeTenant,
  onSignedIn,
}: SignInPageProps) {
  const [challenge, setChallenge] = useState<PendingChallenge | null>(null);

  const step = tenant === null ? 'company' : challenge !== null ? 'twoFactor' : 'credentials';

  return (
    <AuthLayout
      panel={{
        icon: 'ph-duotone ph-coat-hanger',
        heading: 'AttireRent',
        blurb:
          'Bookings, fittings and returns for suit and attire rental — across every branch, through the whole wedding season.',
      }}
      title={
        step === 'company'
          ? 'Find your company'
          : step === 'credentials'
            ? 'Welcome back'
            : 'Enter your code'
      }
      subtitle={
        step === 'company'
          ? 'Enter the company code your administrator gave you'
          : step === 'credentials'
            ? 'Sign in to your AttireRent account'
            : 'Open your authenticator app and enter the six-digit code for AttireRent.'
      }
    >
      {step === 'company' && <CompanyStep onResolved={onTenantResolved} />}

      {step === 'credentials' && tenant !== null && (
        <CredentialsStep
          tenant={tenant}
          onRefreshed={onTenantResolved}
          onChangeTenant={onChangeTenant}
          onSignedIn={onSignedIn}
          onChallenge={setChallenge}
        />
      )}

      {step === 'twoFactor' && challenge !== null && (
        <TwoFactorStep
          challenge={challenge}
          onSignedIn={onSignedIn}
          // Going back throws the challenge away rather than keeping it aside. Re-entering the
          // password costs seconds; a challenge left alive in a tab somebody walked away from does
          // not, and the whole point of the thing is that it expires.
          onBack={() => setChallenge(null)}
        />
      )}

      {/* Development builds only. RAJKOT and MORBI exist only where DevelopmentDataSeeder ran, so
          on production this sent a shop to type codes that are refused — and each refusal counts
          towards the hour-long lookup lockout. Vite replaces the flag at build time, so the
          production bundle does not even carry the text. Theme tokens, for the same reason as the
          company chip below. */}
      {import.meta.env.DEV && (
        <div className="mt-3 rounded-md border border-border-color bg-white px-3 py-2 text-center text-xs text-default">
          Development build. Try <strong className="text-title">RAJKOT</strong> (Enterprise, three
          branches) or <strong className="text-title">MORBI</strong> (Basic — packages and
          multi-branch are locked).
        </div>
      )}
    </AuthLayout>
  );
}

/**
 * Step one: the company code.
 *
 * A wrong code is refused here rather than being carried into a password attempt, which is the
 * point of the split — "that company code was not recognised" is actionable in a way that a
 * blanket "invalid credentials" three fields later is not.
 */
function CompanyStep({ onResolved }: { onResolved: (tenant: TenantEndpoint) => void }) {
  const [busy, setBusy] = useState(false);
  const notify = useNotify();

  // Once the server has stopped answering, there is nothing to be gained by letting someone keep
  // typing codes into a box that cannot succeed for an hour. Held as its own flag rather than
  // derived from a retained error, because the error itself now leaves as a toast and nothing else
  // on this step needs it afterwards.
  const [blocked, setBlocked] = useState(false);

  const submit = async ({ tenantCode }: { tenantCode: string }) => {
    setBusy(true);

    try {
      // Persisting is the caller's job — it owns both the stored copy and the one the API client
      // reads, and splitting that would be how the two come to disagree.
      onResolved(await resolveTenant(tenantCode));
    } catch (caught) {
      notify.error(caught);
      setBlocked(throttledFor(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Form<{ tenantCode: string }>
        layout="vertical"
        requiredMark={false}
        initialValues={{ tenantCode: '' }}
        onFinish={(values) => void submit(values)}
      >
        <Form.Item
          label="Company code"
          name="tenantCode"
          rules={[{ required: true, message: 'Enter your company code.' }]}
        >
          <Input
            size="large"
            autoComplete="organization"
            autoFocus
            disabled={blocked}
            // A real code as the example only where it exists; see the development note below.
            placeholder={import.meta.env.DEV ? 'RAJKOT' : undefined}
            style={{ fontFamily: fontMono }}
          />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          loading={busy}
          disabled={blocked}
          block
        >
          Continue
        </Button>
      </Form>
    </>
  );
}

/**
 * Step two: who you are, at a company already established.
 *
 * The company is shown rather than re-asked, and "Change company" is always reachable — a
 * remembered company that cannot be changed is a device nobody else can sign in on.
 */
function CredentialsStep({
  tenant,
  onRefreshed,
  onChangeTenant,
  onSignedIn,
  onChallenge,
}: {
  tenant: TenantEndpoint;
  onRefreshed: (tenant: TenantEndpoint) => void;
  onChangeTenant: () => void;
  onSignedIn: (auth: AuthResponse) => void;
  /** The password was right, and the account wants a code too. */
  onChallenge: (challenge: PendingChallenge) => void;
}) {
  const [busy, setBusy] = useState(false);
  const notify = useNotify();

  // Once per remembered code, not once per render — the effect's own callback changes the state it
  // depends on, so without this it would re-resolve forever.
  const refreshedFor = useRef<string | null>(null);

  /*
   * Re-resolve a remembered company before its password is typed.
   *
   * What is remembered on the device is a *cache of a server-owned fact*, and the fact can move:
   * a tenant migrated to its own deployment keeps the old address in every browser that ever
   * signed in, and pins it there forever, because remembering the company is precisely what skips
   * the step that would have noticed. The failure is silent and per-device — the sort that gets
   * reported as "it works on my machine".
   *
   * So the remembered record is treated as a starting point rather than an answer. A failure other
   * than "no such company" leaves the cached value in place: if the network is down, the sign-in
   * about to be attempted will fail on its own terms and say so, which is more useful than this
   * screen throwing the company away first.
   */
  useEffect(() => {
    if (refreshedFor.current === tenant.code) {
      return;
    }

    refreshedFor.current = tenant.code;

    const controller = new AbortController();

    void resolveTenant(tenant.code, controller.signal)
      .then(onRefreshed)
      .catch((caught: unknown) => {
        // The company is genuinely gone or was renamed. Sending someone back to step one is the
        // only honest move: there is no company here to sign in to.
        if (isUnknownCompany(caught)) {
          onChangeTenant();
        }
      });

    return () => controller.abort();
  }, [tenant.code, onRefreshed, onChangeTenant]);

  const submit = async (values: Credentials) => {
    setBusy(true);

    try {
      const response = await apiFetch<LoginResponse>('/api/auth/login', {
        method: 'POST',
        // The code travels from the resolved company, not from a field the user could edit
        // between the two steps — otherwise the request could name one company while the API
        // it is addressed to serves another.
        body: { tenantCode: tenant.code, ...values },
        anonymous: true,
      });

      if (isChallenge(response)) {
        onChallenge({ ...response, rememberMe: values.rememberMe });
      } else {
        onSignedIn(response);
      }
    } catch (caught) {
      notify.error(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/*
        Template classes rather than `palette` inline styles: the tokens behind them are redefined
        under `[data-theme="dark"]`, so this follows the theme. A hardcoded hex does not, and left
        a white chip sitting on a dark page.
      */}
      <div className="mb-5 flex items-center justify-between gap-3 rounded-md border border-border-color bg-light px-3 py-2.5">
        <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
          <Typography.Text strong ellipsis className="!text-title">
            {tenant.name}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontFamily: fontMono, fontSize: 12 }}>
            {tenant.code}
          </Typography.Text>
        </Space>

        <Button type="link" size="small" onClick={onChangeTenant} style={{ padding: 0 }}>
          Change company
        </Button>
      </div>

      <Form<Credentials>
        layout="vertical"
        requiredMark={false}
        // Unticked unless the user says otherwise — see the note on Credentials.rememberMe.
        initialValues={{ rememberMe: false }}
        onFinish={(values) => void submit(values)}
      >
        {/*
          One field for two identifiers. It is safe because a username may never begin with a
          digit, so what is typed can only match one of them — and it is kinder than asking which
          kind it is, which is a question about the database rather than about the person.
        */}
        <Form.Item
          label="Username or mobile"
          name="identifier"
          rules={[{ required: true, message: 'Enter your username or mobile number.' }]}
        >
          <Input size="large" autoComplete="username" autoFocus placeholder="rakesh" />
        </Form.Item>

        <Form.Item
          label="Password"
          name="password"
          rules={[{ required: true, message: 'Enter your password.' }]}
        >
          <Input.Password size="large" autoComplete="current-password" />
        </Form.Item>

        {/*
          `valuePropName` is what makes a Checkbox work inside Form.Item at all — without it the
          form binds `value` and the box never appears ticked however many times it is clicked.
        */}
        <Form.Item name="rememberMe" valuePropName="checked">
          <Checkbox>
            Keep me signed in on this device
            <div style={{ fontSize: 12, color: palette.graphite, marginTop: 2 }}>
              Leave this off on a shared counter.
            </div>
          </Checkbox>
        </Form.Item>

        <Button type="primary" htmlType="submit" size="large" loading={busy} block>
          Sign in
        </Button>
      </Form>
    </>
  );
}

/**
 * Step three: six digits from the authenticator, or one recovery code — only reached for an
 * account with 2FA on.
 *
 * One field for both, mirroring the sign-in box that takes a username or a mobile. Which kind of
 * value somebody is holding is obvious to them and irrelevant to the form — the server can tell
 * them apart by looking, so asking would be a question about our storage rather than about them.
 *
 * The company code, identifier and password are gone from the screen. Everything needed to finish
 * is already held server-side against the challenge, and re-showing the password field would
 * invite people to retype a credential that has already been accepted.
 */
function TwoFactorStep({
  challenge,
  onSignedIn,
  onBack,
}: {
  challenge: PendingChallenge;
  onSignedIn: (auth: AuthResponse) => void;
  onBack: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const notify = useNotify();

  const verify = async (code: string) => {
    setBusy(true);

    try {
      onSignedIn(
        await apiFetch<AuthResponse>('/api/auth/login/two-factor', {
          method: 'POST',
          // The tick from the credentials step travels with the exchange: this is the request
          // that actually issues the session, so it is the one that decides the cookie.
          body: {
            challengeToken: challenge.challengeToken,
            code,
            rememberMe: challenge.rememberMe,
          },
          anonymous: true,
        }),
      );
    } catch (caught) {
      notify.error(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Form<{ code: string }>
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => void verify(values.code)}
      >
        <Form.Item
          label="Verification code"
          name="code"
          rules={[{ required: true, message: 'Enter the code from your authenticator app.' }]}
        >
          <Input
            size="large"
            autoFocus
            // one-time-code is what makes a phone offer to fill the SMS or authenticator code
            // it can see, and what stops a password manager offering a password here.
            autoComplete="one-time-code"
            inputMode="text"
            placeholder="123456"
            style={{ fontFamily: fontMono, letterSpacing: '0.08em' }}
          />
        </Form.Item>

        <Button type="primary" htmlType="submit" size="large" loading={busy} block>
          Verify
        </Button>
      </Form>

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Lost your phone? Use one of your recovery codes in the box above.
        </Typography.Text>
      </div>

      <Button type="link" block onClick={onBack} style={{ marginTop: 8 }}>
        Back to sign in
      </Button>
    </>
  );
}
